import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { usePatchUserCustomClaims } from '~/hooks/apis/users/usePatchUserCustomClaims';

import type { UserCustomClaim, UserCustomClaims } from '~/services/UsersService';

import {
  USER_DETAILS_GRID_CONFIG,
  USER_DETAILS_GRID_PAGE_SIZE,
  compareAlphabetically,
  getErrorMessage,
  getUserDetailsGridHeight,
} from './utils';

function formatClaimValue(value: unknown) {
  if (typeof value === 'string') {
    return value;
  }

  return JSON.stringify(value);
}

function parseClaimValue(rawValue: string) {
  const trimmedValue = rawValue.trim();

  if (!trimmedValue.length) {
    return '';
  }

  try {
    return JSON.parse(trimmedValue);
  } catch {
    return trimmedValue;
  }
}

function normalizeCustomClaims(formData: unknown): UserCustomClaims {
  if (Array.isArray(formData)) {
    return formData
      .filter((claim): claim is UserCustomClaim => Boolean(claim) && typeof claim === 'object' && 'name' in claim)
      .map((claim) => ({
        name: String(claim.name),
        value: claim.value,
      }));
  }

  if (formData && typeof formData === 'object') {
    return Object.entries(formData as Record<string, unknown>).map(([name, value]) => ({
      name,
      value,
    }));
  }

  return [];
}

type CustomClaimsFieldUiOptions = {
  accountId?: string;
  customClaims?: UserCustomClaims;
  disabled?: boolean;
  onUserPatched?: (user: App.User) => void;
  title?: string;
};

export default function CustomClaimsField({
  accountId,
  customClaims,
  disabled = false,
  onUserPatched,
  title = 'Custom Claims',
}: CustomClaimsFieldUiOptions) {
  const { enqueueSnackbar } = useSnackbar();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [claimPendingDelete, setClaimPendingDelete] = useState<UserCustomClaim | null>(null);
  const [claimKey, setClaimKey] = useState('');
  const [claimValue, setClaimValue] = useState('');
  const [page, setPage] = useState(0);
  const [localCustomClaims, setLocalCustomClaims] = useState<UserCustomClaims>(normalizeCustomClaims(customClaims));
  const isActionDisabled = Boolean(disabled);
  const patchUserCustomClaimsMutation = usePatchUserCustomClaims(accountId || '');
  const sortedCustomClaims = useMemo(
    () =>
      [...localCustomClaims].sort((leftClaim, rightClaim) => compareAlphabetically(leftClaim.name, rightClaim.name)),
    [localCustomClaims],
  );

  useEffect(() => {
    setLocalCustomClaims(normalizeCustomClaims(customClaims));
  }, [customClaims]);
  const rows = useMemo(
    () =>
      sortedCustomClaims.map((claim) => ({
        id: claim.name,
        ...claim,
        formattedValue: formatClaimValue(claim.value),
      })),
    [sortedCustomClaims],
  );
  const gridHeight = useMemo(() => getUserDetailsGridHeight(rows.length), [rows.length]);
  const columns = useMemo<Array<GridColDef>>(
    () => [
      {
        field: 'name',
        headerName: 'Key',
        flex: 1,
        display: 'flex',
      },
      {
        field: 'formattedValue',
        headerName: 'Value',
        flex: 1,
        display: 'flex',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        align: 'right',
        headerAlign: 'right',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 140,
        renderCell: ({ row }) => (
          <Button
            variant="text"
            color="error"
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            onClick={() => setClaimPendingDelete({ name: row.name, value: row.value })}
            disabled={isActionDisabled || patchUserCustomClaimsMutation.isPending}
            sx={{ mt: -1 }}
          >
            Delete
          </Button>
        ),
      },
    ],
    [isActionDisabled, patchUserCustomClaimsMutation.isPending],
  );

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
    setClaimKey('');
    setClaimValue('');
  };

  const handleAddClaim = async () => {
    const trimmedClaimKey = claimKey.trim();

    if (!trimmedClaimKey) {
      enqueueSnackbar('Claim key is required', { variant: 'error' });
      return;
    }

    if (localCustomClaims.some((claim) => claim.name === trimmedClaimKey)) {
      enqueueSnackbar('Claim key already exists', { variant: 'error' });
      return;
    }

    const nextClaims: UserCustomClaims = [
      ...localCustomClaims,
      {
        name: trimmedClaimKey,
        value: parseClaimValue(claimValue),
      },
    ];

    try {
      const updatedUser = await patchUserCustomClaimsMutation.mutateAsync(nextClaims);
      const updatedCustomClaims = updatedUser.custom_claims ?? nextClaims;
      setLocalCustomClaims(updatedCustomClaims);
      onUserPatched?.({
        ...updatedUser,
        custom_claims: updatedCustomClaims,
      });
      handleCloseAddDialog();
      enqueueSnackbar('Custom claims updated', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Unable to update custom claims: ${getErrorMessage(error)}`, { variant: 'error' });
    }
  };

  const handleDeleteClaim = async () => {
    if (!claimPendingDelete) {
      return;
    }

    const nextClaims = localCustomClaims.filter((claim) => claim.name !== claimPendingDelete.name);

    try {
      const updatedUser = await patchUserCustomClaimsMutation.mutateAsync(nextClaims);
      const updatedCustomClaims = updatedUser.custom_claims ?? nextClaims;
      setLocalCustomClaims(updatedCustomClaims);
      onUserPatched?.({
        ...updatedUser,
        custom_claims: updatedCustomClaims,
      });
      setClaimPendingDelete(null);
      enqueueSnackbar('Custom claims updated', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Unable to update custom claims: ${getErrorMessage(error)}`, { variant: 'error' });
    }
  };

  if (!accountId) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        Custom claims are unavailable because the account ID is missing.
      </Alert>
    );
  }

  return (
    <Box sx={{ pb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
        <Button
          variant="contained"
          onClick={() => setIsAddDialogOpen(true)}
          disabled={isActionDisabled || patchUserCustomClaimsMutation.isPending}
        >
          Add claim
        </Button>
      </Stack>

      {!localCustomClaims.length && (
        <Typography variant="body2" color="text.secondary">
          No custom claims configured.
        </Typography>
      )}

      {localCustomClaims.length > 0 && (
        <Box sx={{ height: gridHeight }}>
          <DataGrid
            rows={rows}
            columns={columns}
            {...USER_DETAILS_GRID_CONFIG}
            initialState={{
              pagination: { paginationModel: { pageSize: USER_DETAILS_GRID_PAGE_SIZE } },
            }}
            pageSizeOptions={[USER_DETAILS_GRID_PAGE_SIZE]}
            paginationModel={{ pageSize: USER_DETAILS_GRID_PAGE_SIZE, page }}
            onPaginationModelChange={({ page }) => setPage(page)}
            disableColumnMenu
            disableRowSelectionOnClick
            slots={{ pagination: GridPagination }}
          />
        </Box>
      )}

      <Dialog open={isAddDialogOpen} onClose={handleCloseAddDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add Custom Claim</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Claim Key"
              value={claimKey}
              onChange={(event) => setClaimKey(event.target.value)}
              disabled={patchUserCustomClaimsMutation.isPending}
              fullWidth
              required
            />
            <TextField
              label="Claim Value"
              value={claimValue}
              onChange={(event) => setClaimValue(event.target.value)}
              disabled={patchUserCustomClaimsMutation.isPending}
              fullWidth
              helperText="Plain text is stored as a string. Valid JSON is parsed as JSON."
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={patchUserCustomClaimsMutation.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddClaim} disabled={patchUserCustomClaimsMutation.isPending}>
            Add claim
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(claimPendingDelete)}
        onClose={patchUserCustomClaimsMutation.isPending ? undefined : () => setClaimPendingDelete(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Custom Claim</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove the custom claim <strong>{claimPendingDelete?.name}</strong> from this account?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClaimPendingDelete(null)} disabled={patchUserCustomClaimsMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteClaim}
            disabled={patchUserCustomClaimsMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
